import React, { useState, useEffect, useCallback } from "react";
import DataGrid from "./components/DataGrid";
import TabBar from "./components/TabBar";
import { getProjectData, DEFAULT_NAMED_RANGES } from "./services/data";
import "./styles/grid.css";

const PROJECTS = [
  { value: "FINANCE", label: "Finance", icon: "📈" },
  { value: "BANKING", label: "Banking", icon: "🏦" },
  { value: "INSURANCE", label: "Insurance", icon: "🛡️" },
];

// Build default project structure
const makeProject = (key) => ({
  salesData: getProjectData(key),
  summaryData: null,
  namedRanges: [...DEFAULT_NAMED_RANGES],
  tabs: [
    { id: `${key}_sales`, name: "Sales Data" },
    { id: `${key}_summary`, name: "Summary" },
  ],
  activeTab: `${key}_sales`,
});

const loadProjects = () => {
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (key.startsWith("agFinance") || key.startsWith("agData_") || key.startsWith("agGrid_"))) {
      localStorage.removeItem(key);
      i--;
    }
  }
  const saved = localStorage.getItem("agFinance_v13");
  if (saved) {
    try {
      const p = JSON.parse(saved);
      if (p.FINANCE && p.BANKING && p.INSURANCE) {
        // Re-inject mock data so we don't exceed storage quota
        p.FINANCE.salesData = getProjectData("FINANCE");
        p.BANKING.salesData = getProjectData("BANKING");
        p.INSURANCE.salesData = getProjectData("INSURANCE");
        return p;
      }
    } catch { }
  }
  return {
    FINANCE: makeProject("FINANCE"),
    BANKING: makeProject("BANKING"),
    INSURANCE: makeProject("INSURANCE"),
  };
};

function Dashboard({ onLogout, dark, setDark }) {
  const [currentProject, setCurrentProject] = useState("FINANCE");
  const [projects, setProjects] = useState(loadProjects);

  useEffect(() => {
    const patchVersion = "agFinance_v13_100k_rows";
    if (localStorage.getItem(patchVersion) !== "true") {
      setProjects(loadProjects());
      localStorage.setItem(patchVersion, "true");
    }
  }, []);

  useEffect(() => {
    try {
      // Strip out the massive 100,000 row arrays before saving to avoid 5MB quota crashes
      const stateToSave = {};
      for (const [k, v] of Object.entries(projects)) {
        stateToSave[k] = { ...v, salesData: null };
      }
      localStorage.setItem("agFinance_v13", JSON.stringify(stateToSave));
    } catch (e) {
      console.warn("Failed to persist data:", e);
    }
  }, [projects]);

  const proj = projects[currentProject] || makeProject(currentProject);
  const salesData = proj.salesData || [];
  const summaryData = proj.summaryData || null;
  const namedRanges = proj.namedRanges || [];
  const tabs = proj.tabs || [];
  const activeTab = proj.activeTab || tabs[0]?.id;

  const isSummaryTab = tabs.find(t => t.id === activeTab)?.name === "Summary";

  const setActiveTab = (tabId) =>
    setProjects(prev => ({ ...prev, [currentProject]: { ...prev[currentProject], activeTab: tabId } }));

  const setTabs = (newTabs) =>
    setProjects(prev => ({ ...prev, [currentProject]: { ...prev[currentProject], tabs: newTabs } }));

  // Called by DataGrid when user edits rows on the Sales Data tab
  const updateSalesData = useCallback((newData) => {
    setProjects(prev => ({
      ...prev,
      [currentProject]: { ...prev[currentProject], salesData: newData },
    }));
  }, [currentProject]);

  const updateSummaryData = useCallback((newData) => {
    setProjects(prev => ({
      ...prev,
      [currentProject]: { ...prev[currentProject], summaryData: newData },
    }));
  }, [currentProject]);

  const updateNamedRanges = useCallback((newData) => {
    setProjects(prev => ({
      ...prev,
      [currentProject]: { ...prev[currentProject], namedRanges: newData },
    }));
  }, [currentProject]);

  const handleLoadTemplate = (e) => {
    const tmpl = e.target.value;
    if (!tmpl) return;
    setProjects(prev => ({
      ...prev,
      [currentProject]: { ...prev[currentProject], salesData: getProjectData(tmpl), namedRanges: [...DEFAULT_NAMED_RANGES] },
    }));
    e.target.value = "";
  };

  // ── Design ──────────────────────────────────────────────────────────────
  const hdr = dark
    ? "linear-gradient(135deg,#0f1629 0%,#131e37 100%)"
    : "linear-gradient(135deg,#1e3a5f 0%,#2563eb 100%)";

  return (
    <div style={{
      height: "100vh", display: "flex", flexDirection: "column",
      overflow: "hidden",
      background: dark ? "#0b0f1e" : "#f0f4f8",
      fontFamily: "'Inter','Segoe UI',sans-serif",
    }}>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <header style={{
        background: hdr, color: "#fff",
        height: "52px", padding: "0 20px",
        display: "flex", alignItems: "center", gap: "14px",
        flexShrink: 0,
        boxShadow: "0 2px 16px rgba(0,0,0,0.35)",
        zIndex: 20,
      }}>
        {/* Logo */}
        <div style={{ display: "flex", alignItems: "center", gap: "9px", flexShrink: 0 }}>
          <div style={{
            width: "32px", height: "32px", borderRadius: "8px",
            background: "rgba(255,255,255,0.14)", border: "1px solid rgba(255,255,255,0.2)",
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: "17px",
          }}>📊</div>
          <div>
            <div style={{ fontSize: "14px", fontWeight: "700", letterSpacing: "-0.02em", lineHeight: 1.2 }}>AG‑Finance Portal</div>
            <div style={{ fontSize: "9px", opacity: 0.55, letterSpacing: "0.07em" }}>SPREADSHEET ENGINE</div>
          </div>
        </div>

        <div style={{ width: "1px", height: "26px", background: "rgba(255,255,255,0.18)", flexShrink: 0 }} />

        {/* Project switcher */}
        <div style={{ display: "flex", alignItems: "center", gap: "5px", flexShrink: 0 }}>
          <span style={{ fontSize: "10px", opacity: 0.6, fontWeight: "600", letterSpacing: "0.06em" }}>PROJECT</span>
          {PROJECTS.map(p => (
            <button key={p.value} onClick={() => setCurrentProject(p.value)}
              style={{
                padding: "4px 10px", border: "none", borderRadius: "6px",
                cursor: "pointer", fontSize: "12px", fontWeight: "600",
                background: currentProject === p.value ? "rgba(255,255,255,0.22)" : "rgba(255,255,255,0.07)",
                color: "#fff", transition: "background 0.15s",
                boxShadow: currentProject === p.value ? "inset 0 0 0 1px rgba(255,255,255,0.25)" : "none",
                display: "flex", alignItems: "center", gap: "4px",
              }}
              onMouseEnter={e => { if (currentProject !== p.value) e.currentTarget.style.background = "rgba(255,255,255,0.13)"; }}
              onMouseLeave={e => { if (currentProject !== p.value) e.currentTarget.style.background = "rgba(255,255,255,0.07)"; }}
            >
              {p.icon} {p.label}
            </button>
          ))}
        </div>

        <div style={{ width: "1px", height: "26px", background: "rgba(255,255,255,0.18)", flexShrink: 0 }} />

        {/* Template loader */}
        <div style={{ display: "flex", alignItems: "center", gap: "6px", flexShrink: 0 }}>
          <span style={{ fontSize: "10px", opacity: 0.6, fontWeight: "600", letterSpacing: "0.06em" }}>TEMPLATE</span>
          <select onChange={handleLoadTemplate} defaultValue=""
            style={{
              padding: "4px 8px", borderRadius: "6px", fontSize: "12px", fontWeight: "500",
              background: "rgba(255,255,255,0.1)", color: "#fff",
              border: "1px solid rgba(255,255,255,0.2)", cursor: "pointer", outline: "none",
            }}>
            <option value="" disabled style={{ color: "#000" }}>Load Data…</option>
            {PROJECTS.map(p => <option key={p.value} value={p.value} style={{ color: "#000" }}>{p.icon} {p.label}</option>)}
          </select>
        </div>

        <div style={{ flex: 1 }} />

        {/* Row count badge */}
        <div style={{
          display: "flex", alignItems: "center", gap: "5px",
          background: "rgba(16,185,129,0.15)", borderRadius: "20px",
          padding: "3px 10px", fontSize: "11px", fontWeight: "600",
          color: "#6ee7b7", border: "1px solid rgba(16,185,129,0.25)",
          flexShrink: 0,
        }}>
          <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#10b981", display: "inline-block", boxShadow: "0 0 6px #10b981" }} />
          {salesData.length} rows
        </div>

        {/* Dark toggle */}
        <button onClick={() => setDark(!dark)} title="Toggle dark/light"
          style={{
            width: "32px", height: "32px", borderRadius: "50%",
            background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)",
            cursor: "pointer", color: "#fff", fontSize: "15px",
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0, transition: "background 0.15s",
          }}
          onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.22)"}
          onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.1)"}
        >
          {dark ? "☀️" : "🌙"}
        </button>

        {/* Sign Out */}
        <button onClick={onLogout}
          style={{
            padding: "5px 13px", borderRadius: "6px", fontSize: "12px", fontWeight: "600",
            background: "rgba(239,68,68,0.15)", color: "#fca5a5",
            border: "1px solid rgba(239,68,68,0.3)", cursor: "pointer",
            flexShrink: 0, transition: "background 0.15s",
          }}
          onMouseEnter={e => e.currentTarget.style.background = "rgba(239,68,68,0.28)"}
          onMouseLeave={e => e.currentTarget.style.background = "rgba(239,68,68,0.15)"}
        >
          Sign Out
        </button>
      </header>

      {/* ── Grid area ──────────────────────────────────── */}
      <main style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
        <DataGrid
          key={`${currentProject}_${activeTab}`}
          rowData={!isSummaryTab ? salesData : (summaryData || [])}
          salesData={salesData}
          summaryData={summaryData}
          namedRanges={namedRanges}
          updateNamedRanges={updateNamedRanges}
          dark={dark}
          tabId={activeTab}
          updateTabData={!isSummaryTab ? updateSalesData : updateSummaryData}
          isSummarySheet={!!isSummaryTab}
        />
      </main>

      {/* ── Tab bar ────────────────────────────────────── */}
      <footer style={{
        flexShrink: 0,
        background: dark ? "#0f1624" : "#f0f4f8",
        borderTop: `1px solid ${dark ? "#1e2843" : "#e2e8f0"}`,
        zIndex: 10,
      }}>
        <TabBar
          tabs={tabs}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          setTabs={setTabs}
          dark={dark}
        />
      </footer>
    </div>
  );
}

export default Dashboard;
